/**
 * useVerification Hook
 * Custom hook for managing seller verification state and actions
 */
import { useState, useEffect, useCallback } from 'react';
import { getVerificationStatus, applyForVerification, recheckVerification, recomputeMetrics } from '../api/verification';

export const useVerification = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const status = await getVerificationStatus();
      setData(status);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching verification status:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const apply = useCallback(async (requestedTier) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await applyForVerification(requestedTier);
      setData(result);
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Error applying for verification:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const recheck = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await recheckVerification();
      setData(result);
      return result;
    } catch (err) {
      if (err.message === 'RATE_LIMIT') {
        setError('Rate limit reached. You can recheck 3 times per day. Please try again later.');
      } else {
        setError(err.message);
      }
      console.error('Error rechecking verification:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await recomputeMetrics();
      // Refresh status after metrics update
      await fetchStatus();
      return result;
    } catch (err) {
      if (err.message === 'RATE_LIMIT') {
        setError('Rate limit reached. You can refresh metrics 3 times per day. Please try again later.');
      } else {
        setError(err.message);
      }
      console.error('Error refreshing metrics:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchStatus]);

  const refresh = useCallback(() => {
    return fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    data,
    isLoading,
    error,
    apply,
    recheck,
    refreshMetrics,
    refresh
  };
};
